import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import axios from 'axios';
import {
    Box,
    Button,
    Menu,
    Container,
    Divider,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Paper,
    TextField,
    Typography,
    IconButton,
    alpha,
    Tooltip,
    TableContainer,
    Grid,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Snackbar,
    Alert,
    Chip,
    TablePagination,
    InputAdornment,
    Autocomplete,
    Checkbox,
    FormControlLabel,
} from '@mui/material';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/material.css';
import {
    MoreVert as MoreVertIcon,
    Refresh as RefreshIcon,
    Inventory as InventoryIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Payment as PaymentIcon,
    Autorenew as AutorenewIcon,
    MoreVert
} from '@mui/icons-material';

// Configure dayjs plugin
dayjs.extend(isSameOrBefore);

const CustomerContractForm = (user) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [employers, setEmployers] = useState([]);
    const [loadingEmployers, setLoadingEmployers] = useState(false); const [formData, setFormData] = useState({
        id: '',
        customerName: '',
        companyName: '',
        email: '',
        phone: '',
        status: 'Active',
        createdBy: '',  // We'll set this from userData
        subscriptionPackage: '',
        isTrial: false,
        createdDate: dayjs().format('YYYY-MM-DD'),
        expirationDate: dayjs().add(1, 'month').format('YYYY-MM-DD'),
        discount: 0,
    });

    const [openFormDialog, setOpenFormDialog] = useState(false);
    const [contracts, setContracts] = useState([]);
    const [contractItems, setContractItems] = useState([]);
    const [subscriptionPackages, setSubscriptionPackages] = useState([]);
    const [loading, setLoading] = useState({
        packages: false,
        contracts: false,
        submit: false,
        items: false
    });
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [contractToDelete, setContractToDelete] = useState(null);
    const [editMode, setEditMode] = useState(false);
    const [isRenewal, setIsRenewal] = useState(false);
    const [priceListItems, setPriceListItems] = useState([]);
    const [userData, setUserData] = useState(null);
    const [salesTeam, setSalesTeam] = useState([]);
    const [searchQuery, setSearchQuery] = useState(''); const [statusFilter, setStatusFilter] = useState('all'); const STATUS_OPTIONS = [
        { value: 'all', label: 'All' },
        { value: 'Paid', label: 'Paid' },
        { value: 'Pending', label: 'Pending' },
        { value: 'Expired', label: 'Expired' }
    ];

    const [touched, setTouched] = React.useState({
        customerName: false
    });
    // Dropdown menu state and handlers for MoreVertIcon actions
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedContract, setSelectedContract] = useState(null);

    const handleMenuOpen = (event, contract) => {
        setAnchorEl(event.currentTarget);
        setSelectedContract(contract);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setSelectedContract(null);
    };
    // Fetch all necessary data on component mount
    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading({
                    packages: true,
                    contracts: true,
                    items: true,
                    submit: false
                });

                const [packagesResponse, contractsResponse, priceListResponse] = await Promise.all([
                    axios.get(`${process.env.REACT_APP_API_URL}/subscription-packages`),
                    axios.get(`${process.env.REACT_APP_API_URL}/customer-contracts`),
                    axios.get(`${process.env.REACT_APP_API_URL}/price-lists`)
                ]);

                setSubscriptionPackages(packagesResponse.data);
                setContracts(Array.isArray(contractsResponse.data?.data) ? contractsResponse.data.data : []);
                setPriceListItems(priceListResponse.data);
            } catch (err) {
                console.error('Error fetching data:', err);
                setError('Failed to load data');
            } finally {
                setLoading({
                    packages: false,
                    contracts: false,
                    items: false,
                    submit: false
                });
            }
        };

        fetchData();
    }, []);


    useEffect(() => {
        const fetchUserData = async () => {
            try {
                const userFromStorage = localStorage.getItem('user');
                if (!userFromStorage) {
                    console.error('No user data in localStorage');
                    return;
                }

                const parsedUser = JSON.parse(userFromStorage);
                if (!parsedUser?.id) {
                    console.error('No user ID found');
                    return;
                }

                // Set the formData.createdBy with the user's name
                setFormData(prev => ({
                    ...prev,
                    createdBy: parsedUser.name
                }));

                const token = localStorage.getItem('authToken');
                const headers = token ? {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                } : {};

                const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/user/${parsedUser.id}`, { headers });

                if (!response.ok) {
                    const errorData = await response.text();
                    console.error('API Error:', {
                        status: response.status,
                        statusText: response.statusText,
                        errorData,
                    });
                    throw new Error(`API request failed with status ${response.status}`);
                }

                const data = await response.json();
                setUserData(data || parsedUser); // fallback to parsedUser if data is empty

            } catch (error) {
                console.error('Fetch error:', error);
                // Fallback to localStorage data
                const fallbackUser = localStorage.getItem('user');
                if (fallbackUser) {
                    setUserData(JSON.parse(fallbackUser));
                }
            } finally {
                setLoading(false);
            }
        };

        fetchUserData();
    }, []); // Runs only once on mount


    useEffect(() => {
        if (userData?.id) {
            setFormData(prev => ({
                ...prev,
                createdBy: userData.name
            }));
        }
    }, [userData]);
    // Using filteredContracts instead of userContracts

    const calculateItemTotals = (item) => {
        // If trial period, return all values as 0
        if (formData.isTrial) {
            return {
                totalPrice: 0,
                freeValue: 0,
                discountPrice: 0,
                chargedTotal: 0,
                finalTotal: 0
            };
        }
        const unitPrice = item.price || 0;
        const totalQuantity = (item.base_quantity || 0) + (item.free_quantity || 0) + (item.extra_quantity || 0);
        const totalPrice = totalQuantity * unitPrice;
        const freeValue = (item.free_quantity || 0) * unitPrice;
        const chargedTotal = ((item.base_quantity || 0) + (item.extra_quantity || 0)) * unitPrice;
        const finalTotal = chargedTotal * (1 - (item.discount || 0) / 100);

        return {
            totalPrice,
            freeValue,
            discountPrice: freeValue,
            chargedTotal,
            finalTotal
        };
    };


    useEffect(() => {
        console.log("Contracts data:", contracts);
    }, [contracts]); const handlePackageChange = async (e) => {
        const { value } = e.target;
        setFormData(prev => ({ ...prev, subscriptionPackage: value }));

        if (!value) {
            setContractItems([]);
            return;
        }

        try {
            setLoading(prev => ({ ...prev, items: true }));

            // Always fetch the new package items when package changes
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/subscription-packages/${value}`);
            console.log("Package data loaded:", response.data); const packageData = response.data;
            const isTrialPackage = packageData.package_name.toLowerCase().includes('trial');

            // Update trial period status and expiration date based on package type
            // For trial packages, auto-check trial period
            // For non-trial packages, force isTrial to false
            setFormData(prev => ({
                ...prev,
                isTrial: isTrialPackage ? true : false,
                expirationDate: isTrialPackage
                    ? dayjs().add(3, 'days').format('YYYY-MM-DD')
                    : dayjs().add(1, 'month').format('YYYY-MM-DD')
            }));

            const itemsWithPackageQuantities = response.data.items.map(item => ({
                ...item,
                item_id: item.item_id || item.id,
                item_name: item.service_item_name || item.item_name || item.id,
                item_description: item.item_description || '',
                unit_of_measure: item.unit_of_measure || '',
                base_quantity: parseInt(item.quantity || 1),
                quantity: parseInt(item.quantity || 1), // This is used for the base quantity input
                free_quantity: parseInt(item.free_quantity || 0),
                extra_quantity: parseInt(item.extra_quantity || 0),
                discount: parseFloat(item.discount || 0),
                price: parseFloat(item.price || 0)
            }));

            console.log("Processed package items:", itemsWithPackageQuantities);
            setContractItems(itemsWithPackageQuantities);
        } catch (err) {
            console.error('Error fetching package items:', err);
            setError('Failed to load package items');
        } finally {
            setLoading(prev => ({ ...prev, items: false }));
        }
    };


    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    }; const handlePhoneChange = (value) => {
        // Ensure phone number starts with + and remove any spaces or extra characters
        const formattedPhone = value ? (value.startsWith('+') ? value : '+' + value) : '';
        setFormData(prev => ({
            ...prev,
            phone: formattedPhone
        }));
    }; const handleQuantityChange = (index, field, value) => {
        const updatedItems = [...contractItems];
        // Convert value to integer and handle edge cases
        const numericValue = Math.max(0, parseInt(value) || 0);
        updatedItems[index][field] = numericValue;

        // If updating base_quantity, also update quantity field
        if (field === 'base_quantity') {
            updatedItems[index].quantity = numericValue;
        }
        // If updating quantity, also update base_quantity field
        if (field === 'quantity') {
            updatedItems[index].base_quantity = numericValue;
        }

        setContractItems(updatedItems);
    };

    const handleItemDiscountChange = (index, value) => {
        const updatedItems = [...contractItems];
        const discount = Math.max(0, Math.min(100, parseFloat(value) || 0));
        updatedItems[index].discount = discount;
        setContractItems(updatedItems);
    };

    const calculateItemTotal = (item) => {
        // If trial period, return 0 for all calculations
        if (formData.isTrial) {
            return 0;
        }
        const chargedQuantity = item.quantity + item.extra_quantity;
        const subtotal = chargedQuantity * (item.price || 0);
        const discountAmount = subtotal * (item.discount / 100);
        return subtotal - discountAmount;
    };

    const calculateContractTotals = () => {
        // If trial period, return all totals as 0
        if (formData.isTrial) {
            return {
                subtotal: "0.00",
                discount: "0.00",
                total: "0.00"
            };
        }
        const subtotal = contractItems.reduce((sum, item) => sum + calculateItemTotal(item), 0);
        const discountAmount = subtotal * (formData.discount / 100);
        const grandTotal = subtotal - discountAmount;

        return {
            subtotal: subtotal.toFixed(2),
            discount: discountAmount.toFixed(2),
            total: grandTotal.toFixed(2)
        };
    };

    const handleSubmit = async (e) => {
        if (e) e.preventDefault();

        // Validate required fields
        if (!formData.customerName || !formData.subscriptionPackage) {
            setError('Customer name and subscription package are required');
            return;
        }

        setLoading(prev => ({ ...prev, submit: true }));
        setError(null);

        try {
            const totals = calculateContractTotals();
            // If trial period, set total to 0
            const grandTotal = formData.isTrial ? 0 : parseFloat(parseFloat(totals.total).toFixed(2));

            // Prepare contract data with proper mapping
            // Provide safe fallbacks for customer_id / company_id: prefer explicit formData, then employers[0]
            const fallbackCustomerId = formData.customer_id || (employers && employers[0] && (employers[0].customer_id || employers[0].id)) || null;
            const fallbackCompanyId = (formData.companyId !== '' && formData.companyId !== undefined) ? parseInt(formData.companyId) : ((employers && employers[0] && (employers[0].companyid || employers[0].company_id)) || null);

            const contractData = {
                id: formData.id || undefined, // Use undefined to avoid sending empty id
                contract_id: formData.contract_id || undefined,
                customer_id: fallbackCustomerId,

                company_id: fallbackCompanyId,
                customerName: formData.customerName,
                companyName: formData.companyName || '',
                email: formData.email || '',
                phone: formData.phone || '',
                status: formData.status,
                createdBy: formData.createdBy,
                subscriptionPackage: formData.subscriptionPackage,
                createdDate: formData.createdDate,
                expirationDate: formData.expirationDate,
                discount: parseFloat(formData.discount || 0),
                grandTotal: grandTotal,
                autoRenewal: formData.autoRenewal || false,
                items: contractItems.map(item => ({
                    item_id: item.id,
                    item_name: item.item_name || item.service_item_name,
                    item_description: item.item_description || '',
                    unit_of_measure: item.unit_of_measure || '',
                    price: parseFloat(item.price || 0),
                    base_quantity: parseInt(item.base_quantity || 0),
                    free_quantity: parseInt(item.free_quantity || 0),
                    extra_quantity: parseInt(item.extra_quantity || 0),
                    discount: parseFloat(item.discount || 0)
                }))
            };

            const url = editMode
                ? `${process.env.REACT_APP_API_URL}/customer-contracts/${formData.id}`
                : `${process.env.REACT_APP_API_URL}/customer-contracts`;

            const method = editMode ? 'put' : 'post';

            // Final safety: ensure customer_id / company_id are set from any available source
            const lookupByName = (name) => (employers && employers.find(emp => emp.name === name));
            const finalCustomerId = contractData.customer_id || formData.customer_id || formData.customerId || (lookupByName(contractData.customerName) && (lookupByName(contractData.customerName).customer_id || lookupByName(contractData.customerName).id)) || (employers && employers[0] && (employers[0].customer_id || employers[0].id)) || (selectedContract && (selectedContract.customer_id || selectedContract.id)) || null;
            const finalCompanyId = contractData.company_id || formData.companyId || formData.company_id || (lookupByName(contractData.customerName) && (lookupByName(contractData.customerName).companyid || lookupByName(contractData.customerName).company_id)) || (employers && employers[0] && (employers[0].companyid || employers[0].company_id)) || (selectedContract && (selectedContract.company_id || selectedContract.companyid)) || null;
            contractData.customer_id = finalCustomerId;
            contractData.company_id = finalCompanyId;

            console.log(`Submitting contract via ${method.toUpperCase()} to ${url}`);
            console.log('Final contractData before submit:', JSON.parse(JSON.stringify(contractData)));
            const response = await axios[method](url, contractData);
            console.log("Server Response:", response.data);

            // Refresh contracts after submission
            await refreshContracts();

            setSuccess(editMode ? 'Contract updated successfully' : 'Contract created successfully');
            setOpenFormDialog(false);  // Close dialog after successful submit
            resetForm();
        } catch (error) {
            console.error('Error saving contract:', error);
            const errorMessage = error.response?.data?.message ||
                error.response?.data?.error ||
                error.message ||
                'Failed to save contract';
            setError(`Error ${editMode ? 'updating' : 'creating'} contract: ${errorMessage}`);
        } finally {
            setLoading(prev => ({ ...prev, submit: false }));
        }
    };



    const handleEdit = async (contract) => {
        try {
            setLoading(prev => ({ ...prev, items: true }));

            // Fetch contract and price list data
            const [contractResponse, priceListResponse] = await Promise.all([
                axios.get(`${process.env.REACT_APP_API_URL}/customer-contracts/${contract.id}`),
                axios.get(`${process.env.REACT_APP_API_URL}/price-lists`)
            ]);

            const contractData = contractResponse.data.data;
            const priceList = priceListResponse.data || [];

            // Set form data from contract            // Set the initial employer for the Autocomplete
            setEmployers([{
                id: contractData.id,
                name: contractData.customer_name,
                company_name: contractData.company_name,
                email: contractData.email,
                phone: contractData.phone
            }]);

            setFormData({
                id: contractData.id || null,
                customerName: contractData.customer_name || '',
                companyName: contractData.company_name || '',
                email: contractData.email || '',
                phone: contractData.phone || '',
                status: contractData.status || 'Active',
                createdBy: contractData.created_by || '',
                subscriptionPackage: contractData.subscription_package_id || '',
                createdDate: contractData.created_date
                    ? dayjs(contractData.created_date).format('YYYY-MM-DD')
                    : dayjs().format('YYYY-MM-DD'),
                expirationDate: contractData.expiration_date
                    ? dayjs(contractData.expiration_date).format('YYYY-MM-DD')
                    : dayjs().add(1, 'month').format('YYYY-MM-DD'),
                discount: parseFloat(contractData.discount_rate || 0),
                grandTotal: parseFloat(contractData.grand_total || 0)
            });

            // Process items with correct quantities
            let processedItems = (contractData.items || []).map(item => ({
                id: item.id,
                item_id: item.price_list_item_id,
                service_item_name: item.service_item_name || item.item_name || '',
                item_name: item.service_item_name || item.item_name || '',
                item_description: item.item_description || '',
                unit_of_measure: item.unit_of_measure || '',
                price: parseFloat(item.price || 0),
                base_quantity: parseInt(item.base_quantity || 0),
                quantity: parseInt(item.base_quantity || 0), // Set quantity same as base_quantity
                free_quantity: parseInt(item.free_quantity || 0),
                extra_quantity: parseInt(item.extra_quantity || 0),
                discount: parseFloat(item.discount || 0)
            }));

            // If no contract items, but subscription package is selected, fetch package items
            if (processedItems.length === 0 && contractData.subscription_package_id) {
                try {
                    const packageResponse = await axios.get(`${process.env.REACT_APP_API_URL}/subscription-packages/${contractData.subscription_package_id}`);
                    const packageData = packageResponse.data;
                    processedItems = (packageData.items || []).map(item => ({
                        id: item.id || item.item_id,
                        item_id: item.item_id || item.id,
                        service_item_name: item.service_item_name || item.item_name || '',
                        item_name: item.service_item_name || item.item_name || '',
                        item_description: item.item_description || '',
                        unit_of_measure: item.unit_of_measure || '',
                        price: parseFloat(item.price || 0),
                        base_quantity: parseInt(item.quantity || 1),
                        quantity: parseInt(item.quantity || 1),
                        free_quantity: parseInt(item.free_quantity || 0),
                        extra_quantity: parseInt(item.extra_quantity || 0),
                        discount: parseFloat(item.discount || 0)
                    }));
                } catch (err) {
                    console.error('Error fetching package items for edit:', err);
                }
            }

            setContractItems(processedItems);
            setEditMode(true);
            setIsRenewal(false);
            setOpenFormDialog(true);

        } catch (err) {
            console.error('Error loading contract:', err);
            setError('Failed to load contract details');
        } finally {
            setLoading(prev => ({ ...prev, items: false }));
        }
    };

    const handleDelete = async () => {
        try {
            await axios.delete(`${process.env.REACT_APP_API_URL}/customer-contracts/${contractToDelete.id}`);

            // Refresh contracts after deletion
            await refreshContracts();

            setSuccess('Contract deleted successfully');
        } catch (error) {
            console.error('Error deleting contract:', error);
            setError('Failed to delete contract');
        } finally {
            setOpenDeleteDialog(false);
            setContractToDelete(null);
        }
    }; const resetForm = () => {
        // Get the current user's name from localStorage
        const userFromStorage = localStorage.getItem('user');
        const userName = userFromStorage ? JSON.parse(userFromStorage).name : '';

        setFormData({
            id: '',
            customerName: '',
            companyName: '',
            email: '',
            phone: '',
            status: 'Active',
            createdBy: userName, // Use the user's name
            subscriptionPackage: '',
            isTrial: false,
            createdDate: dayjs().format('YYYY-MM-DD'),
            expirationDate: dayjs().add(1, 'month').format('YYYY-MM-DD'), // Default to 1 month
            discount: 0,
        });
        setContractItems([]);
        setEditMode(false);
        setIsRenewal(false);
        setOpenFormDialog(false);
    };

    const getSelectedPackageName = () => {
        if (!formData.subscriptionPackage) return '';
        const selectedPackage = subscriptionPackages.find(pkg => pkg.id === formData.subscriptionPackage);
        return selectedPackage ? selectedPackage.package_name : '';
    };

    const handleCloseSnackbar = () => {
        setError(null);
        setSuccess(null);
    };

    const calculateTotalQuantity = (item) => {
        return (item.quantity || 0) + (item.free_quantity || 0) + (item.extra_quantity || 0);
    }; const refreshContracts = async () => {
        try {
            setLoading(prev => ({ ...prev, contracts: true }));

            // Always fetch all contracts
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/customer-contracts`);
            const allContracts = response.data.data || [];

            // If user is a Sales Manager, also fetch their team's contracts
            if (userData?.role === 'Sales Manager') {
                const token = localStorage.getItem('authToken');
                const teamResponse = await axios.get(`${process.env.REACT_APP_API_URL}/users/my-sales-teamcontracts`, {
                    headers: { Authorization: `Bearer ${token}` }
                });

                if (teamResponse.data.success) {
                    const teamContracts = teamResponse.data.salesTeam.reduce((acc, member) => {
                        return [...acc, ...(member.contracts || [])];
                    }, []);

                    // Combine contracts and remove duplicates based on contract ID
                    const combinedContracts = [...allContracts, ...teamContracts];
                    const uniqueContracts = combinedContracts.filter((contract, index, self) =>
                        index === self.findIndex((c) => c.id === contract.id)
                    );

                    setContracts(uniqueContracts);
                    return;
                }
            }

            setContracts(allContracts);
        } catch (err) {
            setError('Failed to refresh contracts');
            console.error('Error refreshing contracts:', err);
        } finally {
            setLoading(prev => ({ ...prev, contracts: false }));
        }
    };
    useEffect(() => {
        refreshContracts();
    }, []);



    const fetchSalesTeam = async () => {
        try {
            setLoading(true);
            setError(null);
            const token = localStorage.getItem('authToken');

            const response = await axios.get(`${process.env.REACT_APP_API_URL}/users/my-sales-teamcontracts`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            console.log("Sales Team API Response:", response.data); // 👈 Log the full response
            if (response.data.success) {
                setSalesTeam(response.data.salesTeam || []);
            } else {
                throw new Error(response.data.message || 'Failed to load team');
            }
        } catch (err) {
            const errorMsg = err.response?.data?.message || err.message || 'Network error';
            setError(errorMsg);
            // showSnackbar(errorMsg);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSalesTeam();
    }, []); const handlePayment = (contractId) => {
        window.location.href = `/payment/create-session/${contractId}`;
    };

    // Pagination handlers
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Reset pagination when filters or data changes
    useEffect(() => {
        setPage(0);
    }, [userData, contracts, searchQuery, statusFilter]);

    // Filter contracts based on user role and permissions
    const filteredContracts = useMemo(() => {
        if (!userData || !contracts || !Array.isArray(contracts)) {
            return [];
        }

        switch (userData.role) {
            case 'Admin':
                // Admin can see all contracts
                return contracts;

            case 'Sales Manager':
                // Sales Manager can see their own contracts and their team's contracts
                return contracts.filter(contract =>
                    contract.created_by === userData.name ||
                    (salesTeam && salesTeam.some(member => member.name === contract.created_by))
                );

            case 'Sales Person':
                // Sales Person can only see their own contracts
                return contracts.filter(contract =>
                    contract.created_by === userData.name
                );

            default:
                return [];
        }
    }, [contracts, userData, salesTeam]);

    // Search and status filtering
    const searchedContracts = useMemo(() => {
        if (!filteredContracts) return [];

        return filteredContracts.filter(contract => {
            // Search filter
            const searchTerms = searchQuery.toLowerCase();
            const matchesSearch = !searchQuery ||
                (contract.customer_name && contract.customer_name.toLowerCase().includes(searchTerms)) ||
                (contract.company_name && contract.company_name.toLowerCase().includes(searchTerms)) ||
                (contract.email && contract.email.toLowerCase().includes(searchTerms)) ||
                (contract.id && contract.id.toString().includes(searchTerms));

            // Determine contract status
            let contractStatus;
            if (contract.auto_renewal === 1 && contract.payment_status === 'paid') {
                contractStatus = 'Active';
            } else if (contract.expiration_date && dayjs(contract.expiration_date).isBefore(dayjs(), 'day')) {
                contractStatus = 'Expired';
            } else if (contract.payment_status === 'paid') {
                contractStatus = 'Paid';
            } else {
                contractStatus = 'Pending';
            }

            // Check if contract has a child (renewed)
            let hasChild = false;
            try {
                hasChild = Array.isArray(contracts) && contracts.some(c => String(c.parent_contract_id || c.parent_contract || '') === String(contract.id));
                if (hasChild) {
                    contractStatus = 'Expired';
                }
            } catch (err) {
                // ignore lookup errors
            }

            contract.current_status = contractStatus;

            // Exclude expired parent contracts that have a child contract
            if (contractStatus === 'Expired' && hasChild) {
                return false;
            }

            // Status filter
            let matchesStatus = true;
            // Default: show all except excluded above
            if (statusFilter !== 'all') {
                matchesStatus = contractStatus === statusFilter;
            }

            return matchesSearch && matchesStatus;
        });
    }, [filteredContracts, searchQuery, statusFilter, contracts]);

    const calculateSubtotal = () => {
        if (!contractItems || contractItems.length === 0) return 0;
        return contractItems.reduce((total, item) => {
            const price = parseFloat(item.price) || 0;
            const totalQty = parseFloat(item.totalQty) || 0;
            return total + (price * totalQty);
        }, 0);
    };

    const calculateFreeValue = () => {
        if (!contractItems || contractItems.length === 0) return 0;
        return contractItems.reduce((total, item) => {
            const price = parseFloat(item.price) || 0;
            const freeQty = parseFloat(item.freeQty) || 0;
            return total + (price * freeQty);
        }, 0);
    };

    const calculateDiscount = () => {
        const subtotal = calculateSubtotal() - calculateFreeValue();
        const discountPercentage = parseFloat(formData.discount) || 0;
        return (subtotal * discountPercentage) / 100;
    };

    const calculateGrandTotal = () => {
        const subtotal = calculateSubtotal();
        const freeValue = calculateFreeValue();
        const discount = calculateDiscount();
        return Math.max(0, subtotal - freeValue - discount);
    }; const handleContractClick = (contractId) => {
        if (contractId) {
            navigate(`/contract-details/${contractId}`);
        } else {
            setError("Invalid contract ID");
        }
    };





    // Function to handle employer selection
    const handleEmployerSelection = (event, value) => {
        if (value) {
            // Update form data with the selected employer's information
            setFormData(prev => ({
                ...prev,
                customerName: value.name || '',
                companyName: value.employer_name || value.company_name || '',
                customer_id: value.id || '',
                companyId: value.companyid || '', // <-- Set companyId here
                email: value.email || '',
                phone: value.phone || value.mobile || '',
            }));
        } else {
            // Clear the fields if no employer is selected
            setFormData(prev => ({
                ...prev,
                customerName: '',
                companyName: '',
                customer_id: '',
                companyId: '',
                companyName: '',
                email: '',
                phone: '',
            }));
        }
    };

    // Function to handle employer search with debounce
    const handleSearchEmployer = useMemo(() => {
        const searchEmployers = async (searchQuery) => {
            try {
                setLoadingEmployers(true);
                const response = await axios.get(`https://crmapi.jobsnprofiles.com/admin/get_employers/${searchQuery}`, {
                    headers: {
                        'Content-Type': 'application/json',
                    }
                }); if (response.data?.success === 1 && response.data?.data?.users) {
                    const transformedData = response.data.data.users.map(user => ({
                        id: user.id,
                        name: user.full_name || '',
                        company_name: user.company_name || '',
                        customer_id: user.id || '',
                        companyid: user.companyid,
                        email: user.username || '',
                        phone: user.mobile || '',
                        address: (user.address1 || '') + (user.address2 ? ` ${user.address2}` : ''),
                        state: user.state || '',
                        country: user.country || '',
                        city: user.city || '',
                        zipcode: user.zipcode || ''
                    }));
                    setEmployers(transformedData);
                } else {
                    setEmployers([]);
                }
            } catch (error) {
                console.error('Error searching employers:', error);
                setEmployers([]);
            } finally {
                setLoadingEmployers(false);
            }
        };

        let timeoutId;
        return (searchQuery) => {
            if (searchQuery.length < 3) {
                setEmployers([]);
                return;
            }
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => {
                searchEmployers(searchQuery);
            }, 500); // 500ms delay
        };
    }, []);

    const handleBlur = (field) => {
        setTouched(prev => ({ ...prev, [field]: true }));
    };

    // Auto-renewal logic
    useEffect(() => {
        const autoRenewContracts = async () => {
            try {
                const expiredContracts = contracts.filter(contract => dayjs(contract.expiration_date).isBefore(dayjs()));

                for (const contract of expiredContracts) {
                    await axios.post(`${process.env.REACT_APP_API_URL}/customer-contracts/${contract.id}/renew`);
                }

                if (expiredContracts.length > 0) {
                    setSuccess(`${expiredContracts.length} contract(s) auto-renewed successfully.`);
                    await refreshContracts();
                }
            } catch (error) {
                console.error('Error during auto-renewal:', error);
                setError('Failed to auto-renew contracts.');
            }
        };

        const intervalId = setInterval(autoRenewContracts, 24 * 60 * 60 * 1000); // Adjusted to align with 2:15 PM

        return () => clearInterval(intervalId); // Cleanup on component unmount
    }, [contracts]);

    return (
        <Box sx={{ margin: 'auto', padding: 0, bgcolor: '#f8fafc', }}>
            <Container maxWidth="xl" sx={{ py: 1 }}>
                {/* Header Section */}
                <Box sx={{ mb: 4 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Box>
                            <Typography variant="h4" component="h1" sx={{
                                fontWeight: 700,
                                color: theme.palette.text.primary,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2
                            }}>
                                <InventoryIcon fontSize="large" color="primary" />
                                Customer Contract Management
                            </Typography>
                            <Typography variant="subtitle1" color="text.secondary" sx={{ mt: 1 }}>
                                Create and manage customer contracts with subscription packages
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<InventoryIcon />}
                                onClick={() => {
                                    resetForm();
                                    setOpenFormDialog(true);
                                }}
                                sx={{
                                    px: 3,
                                    py: 1,
                                    backgroundColor: theme.palette.primary.main,
                                    '&:hover': {
                                        backgroundColor: theme.palette.primary.dark,
                                    },
                                }}
                            >
                                New Contract
                            </Button>
                            <Tooltip title="Refresh data">
                                <IconButton
                                    onClick={refreshContracts}
                                    color="primary"
                                    size="large"
                                    sx={{
                                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                                        '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.2) }
                                    }}
                                >
                                    <RefreshIcon />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </Box>
                </Box>

                {/* Error/Success Messages */}
                {error && (
                    <Snackbar open={!!error} autoHideDuration={6000} onClose={handleCloseSnackbar}>
                        <Alert onClose={handleCloseSnackbar} severity="error" sx={{ width: '100%' }}>
                            {error}
                        </Alert>
                    </Snackbar>
                )}

                {success && (
                    <Snackbar open={!!success} autoHideDuration={6000} onClose={handleCloseSnackbar}>
                        <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
                            {success}
                        </Alert>
                    </Snackbar>
                )}

                <Grid container spacing={3}>
                    {/* Contracts List Section - Now full width */}
                    <Grid item xs={12} sx={{ width: '100%', }}>
                        <Paper
                            elevation={3}
                            sx={{
                                padding: 3,
                                height: '100%',

                                display: 'flex',
                                flexDirection: 'column',
                                borderRadius: 2,
                                boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    mb: 2,
                                    gap: 2
                                }}
                            >
                                <Typography variant="h6" fontWeight={700}>
                                    Contracts
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    <TextField
                                        placeholder="Search by name, email, company..."
                                        variant="outlined"
                                        size="small"
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <SearchRoundedIcon color="action" />
                                                </InputAdornment>
                                            ),
                                            sx: { borderRadius: 1 }
                                        }}
                                        sx={{ width: 300 }}
                                    />
                                    <FormControl size="small" sx={{ minWidth: 150 }}>
                                        <Select
                                            value={statusFilter}
                                            onChange={(e) => setStatusFilter(e.target.value)}
                                            displayEmpty
                                            variant="outlined"
                                        >
                                            {STATUS_OPTIONS.map((option) => (
                                                <MenuItem key={option.value} value={option.value}>
                                                    {option.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Box>
                            </Box>

                            <TableContainer
                                sx={{
                                    flex: 1,
                                    maxHeight: 'calc(100vh - 300px)',
                                    '& .MuiTableCell-root': {
                                        py: 1.5,
                                    },
                                }}
                            >
                                <Table stickyHeader size="small">
                                    <TableHead>
                                        <TableRow
                                            sx={{
                                                '& .MuiTableCell-root': {
                                                    backgroundColor: '#056bbd',
                                                    color: theme.palette.common.white,
                                                    fontWeight: 600,
                                                },
                                            }}
                                        >
                                            {/* <TableCell>Manger ID</TableCell> */}
                                            <TableCell>Contract ID</TableCell>
                                            <TableCell>Customer</TableCell>
                                            <TableCell>Company</TableCell>
                                            <TableCell>Email</TableCell>
                                            <TableCell>Status</TableCell>
                                            {/* <TableCell>Created By</TableCell> */}
                                            <TableCell>Created</TableCell>
                                            <TableCell>Expired </TableCell>
                                            <TableCell>Auto Renewal</TableCell>
                                            <TableCell align="right">Total</TableCell>
                                            <TableCell align="center">Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {loading.contracts ? (
                                            <TableRow>
                                                <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                                                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                                        <CircularProgress size={40} />
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        ) : searchedContracts.length > 0 ? (
                                            searchedContracts
                                                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                                .map((contract, index) => (
                                                    <TableRow
                                                        key={contract.id}
                                                        hover
                                                        sx={{
                                                            '&:last-child td': { borderBottom: 0 },
                                                            backgroundColor: dayjs(contract.expiration_date).isBefore(dayjs(), 'day') ? alpha('#ff0000', 0.05) : 'inherit'
                                                        }}
                                                    >
                                                        <TableCell>
                                                            <Typography
                                                                fontWeight={500}
                                                                sx={{
                                                                    cursor: 'pointer',
                                                                    '&:hover': {
                                                                        color: theme.palette.primary.main,
                                                                        textDecoration: 'underline'
                                                                    }
                                                                }}
                                                                onClick={() => handleContractClick(contract.id)}
                                                            >
                                                                {contract.id || 'N/A'}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography
                                                                fontWeight={500}
                                                                sx={{
                                                                    cursor: 'pointer',
                                                                    '&:hover': {
                                                                        color: theme.palette.primary.main,
                                                                        textDecoration: 'underline'
                                                                    }
                                                                }}
                                                                onClick={() => handleContractClick(contract.id)}
                                                            >
                                                                {contract.customer_name || 'N/A'}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell title={contract.company_name || 'N/A'}>{contract.company_name.slice(0, 20) +
                                                            (contract.company_name.length > 20 ? '...' : '') || 'N/A'}</TableCell>
                                                        <TableCell>{contract.email}</TableCell>
                                                        <TableCell>
                                                            <Chip
                                                                label={dayjs(contract.expiration_date).isBefore(dayjs(), 'day') ? 'Expired' : contract.current_status}
                                                                color={dayjs(contract.expiration_date).isBefore(dayjs(), 'day') ? 'error' :
                                                                    contract.current_status === 'Paid' ? 'success' : 'warning'}
                                                                size="small"
                                                                sx={{ fontWeight: 500, minWidth: 80 }}
                                                            />
                                                        </TableCell>
                                                        {/* <TableCell>{contract.created_by || 'N/A'}</TableCell> */}
                                                        <TableCell>
                                                            {dayjs(contract.created_date).format('DD MMM YYYY')}
                                                        </TableCell>
                                                        <TableCell>
                                                            {dayjs(contract.expiration_date).format('DD MMM YYYY')}
                                                        </TableCell>
                                                        <TableCell>
                                                            {contract.auto_renewal === 1 || contract.autoRenewal === true ? (
                                                                <Chip label="Enabled" color="success" size="small" />
                                                            ) : (
                                                                <Chip label="Disabled" color="default" size="small" />
                                                            )}
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            <Typography fontWeight={600}>
                                                                ${contract.grand_total || '0.00'}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell align="center">
                                                            <>
                                                                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                                                                    <IconButton
                                                                        onClick={(e) => handleMenuOpen(e, contract)}
                                                                        size="small"
                                                                    >
                                                                        <MoreVertIcon />
                                                                    </IconButton>
                                                                  
                                                                </Box>
                                                                <Menu
                                                                    anchorEl={anchorEl}
                                                                    open={Boolean(anchorEl)}
                                                                    onClose={handleMenuClose}
                                                                >
                                                                    <MenuItem onClick={() => { handleEdit(selectedContract); handleMenuClose(); }}>
                                                                        <EditIcon fontSize="small" sx={{ mr: 1, color: '#1976d2' }} /> Edit
                                                                    </MenuItem>
                                                                    <MenuItem onClick={() => {
                                                                        setContractToDelete(selectedContract);
                                                                        setOpenDeleteDialog(true);
                                                                        handleMenuClose();
                                                                    }}>
                                                                        <DeleteIcon fontSize="small" sx={{ mr: 1, color: '#d32f2f' }} /> Delete
                                                                    </MenuItem>
                                                                    <MenuItem onClick={() => { window.open(`/payment/${selectedContract?.id}`, '_blank'); handleMenuClose(); }}>
                                                                        <PaymentIcon fontSize="small" sx={{ mr: 1, color: '#388e3c' }} /> Payment
                                                                    </MenuItem>
                                                                    {/* Cancel Auto Renewal */}
                                                                    {(selectedContract?.auto_renewal === 1 || selectedContract?.autoRenewal === true) && selectedContract?.email && selectedContract?.email.includes('@') ? (
                                                                        <MenuItem onClick={async () => {
                                                                            try {
                                                                                await axios.post(`${process.env.REACT_APP_API_URL}/customer-contracts/${selectedContract.id}/cancel-autorenewal`);
                                                                                setSuccess('Auto-renewal cancelled successfully');
                                                                                await refreshContracts();
                                                                            } catch (error) {
                                                                                setError('Failed to cancel auto-renewal');
                                                                            }
                                                                            handleMenuClose();
                                                                        }}>
                                                                            <AutorenewIcon fontSize="small" sx={{ mr: 1 }} color="warning" /> Cancel Auto Renewal
                                                                        </MenuItem>
                                                                    ) : (
                                                                        (selectedContract?.auto_renewal === 1 || selectedContract?.autoRenewal === true) && (
                                                                            <MenuItem disabled>
                                                                                <AutorenewIcon fontSize="small" sx={{ mr: 1 }} color="warning" /> Cannot cancel auto renewal: No valid email
                                                                            </MenuItem>
                                                                        )
                                                                    )}
                                                                    {/* Renew Contract */}
                                                                    {selectedContract && dayjs(selectedContract.expiration_date).isBefore(dayjs()) && (
                                                                        <MenuItem
                                                                          
                                                                        onClick={async () => {
                                                                            try {
                                                                                setLoading(prev => ({ ...prev, items: true }));
                                                                                const contractResponse = await axios.get(`${process.env.REACT_APP_API_URL}/customer-contracts/${selectedContract.id}`);
                                                                                const contractData = contractResponse.data.data;
                                                                                // Ensure the Autocomplete has an employer option matching the customer
                                                                                setEmployers([{
                                                                                    id: contractData.customer_id || contractData.id || selectedContract.id,
                                                                                    name: contractData.customer_name || selectedContract.customer_name,
                                                                                    company_name: contractData.company_name || selectedContract.company_name,
                                                                                    email: contractData.email || selectedContract.email,
                                                                                    phone: contractData.phone || selectedContract.phone,
                                                                                    // Use the exact customer id from the API response if available
                                                                                    customer_id: contractData.customer_id || contractData.id || selectedContract.id,
                                                                                    companyid: contractData.company_id || selectedContract.companyid || selectedContract.company_id
                                                                                }]);

                                                                                setFormData({
                                                                                    id: null,
                                                                                    customerName: selectedContract.customer_name,
                                                                                    companyName: selectedContract.company_name,
                                                                                    email: selectedContract.email,
                                                                                    phone: selectedContract.phone,
                                                                                    status: 'Active',
                                                                                    createdBy: userData?.name,
                                                                                    subscriptionPackage: selectedContract.subscription_package_id,
                                                                                    createdDate: dayjs().format('YYYY-MM-DD'),
                                                                                    expirationDate: dayjs().add(1, 'month').format('YYYY-MM-DD'),
                                                                                    discount: selectedContract.discount_rate || 0,
                                                                                    // Ensure customer_id and companyId are set so the API receives required ids on renew
                                                                                    customer_id: contractData.customer_id || selectedContract.customer_id || selectedContract.customer_id || selectedContract.id,
                                                                                    companyId: contractData.company_id || selectedContract.company_id || selectedContract.companyid || null,
                                                                                });
                                                                                if (contractData.items && contractData.items.length > 0) {
                                                                                    const mappedItems = contractData.items.map(item => ({
                                                                                        id: null,
                                                                                        // The backend expects item_id / price_list_item_id depending on create vs update
                                                                                        item_id: item.price_list_item_id || item.item_id || item.id,
                                                                                        price_list_item_id: item.price_list_item_id || item.item_id || item.id,
                                                                                        item_name: item.service_item_name || item.item_name,
                                                                                        item_description: item.item_description,
                                                                                        unit_of_measure: item.unit_of_measure,
                                                                                        price: parseFloat(item.price) || 0,
                                                                                        quantity: parseInt(item.base_quantity) || 0,
                                                                                        base_quantity: parseInt(item.base_quantity) || 0,
                                                                                        free_quantity: parseInt(item.free_quantity) || 0,
                                                                                        extra_quantity: parseInt(item.extra_quantity) || 0,
                                                                                        discount: parseFloat(item.discount) || 0
                                                                                    }));
                                                                                    setContractItems(mappedItems);
                                                                                }
                                                                                setEditMode(false);
                                                                                setIsRenewal(true);
                                                                                setOpenFormDialog(true);
                                                                            } catch (err) {
                                                                                console.error('Error loading contract for renewal:', err);
                                                                                setError('Failed to load contract details for renewal');
                                                                            } finally {
                                                                                setLoading(prev => ({ ...prev, items: false }));
                                                                            }
                                                                            handleMenuClose();
                                                                        
                                                                        }}>
                                                                            <AutorenewIcon fontSize="small" sx={{ mr: 1 }} color="primary"  /> Renew Contract
                                                                        </MenuItem>
                                                                    )}
                                                                </Menu>
                                                            </>
                                                        </TableCell>
                                                    
                                                    </TableRow>
                                                ))
                                        ) : (
                                            <TableRow>
                                                <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            alignItems: 'center',
                                                            gap: 1,
                                                        }}
                                                    >
                                                        <InventoryIcon color="disabled" fontSize="large" />
                                                        <Typography color="text.secondary">No contracts found</Typography>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <TablePagination
                                rowsPerPageOptions={[5, 10, 25]}
                                component="div"
                                count={searchedContracts.length}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                sx={{
                                    borderTop: '1px solid',
                                    borderColor: 'divider',
                                    '& .MuiTablePagination-select': {
                                        marginRight: 1
                                    },
                                    '& .MuiTablePagination-selectLabel': {
                                        marginRight: 1
                                    },
                                    '& .MuiTablePagination-displayedRows': {
                                        margin: '0 16px'
                                    }
                                }}
                                labelDisplayedRows={({ from, to, count }) => (
                                    <Tooltip title="Total number of contracts shown">
                                        <span>{`${from}-${to} of ${count}`}</span>
                                    </Tooltip>
                                )}
                                labelRowsPerPage={
                                    <Tooltip title="Number of contracts per page">
                                        <span>Contracts per page:</span>
                                    </Tooltip>
                                }
                            />
                        </Paper>
                    </Grid>
                </Grid>

                {/* Form Dialog */}
                <Dialog
                    open={openFormDialog}
                    onClose={() => setOpenFormDialog(false)}
                    maxWidth="lg"
                    fullWidth
                    PaperProps={{
                        sx: {
                            borderRadius: 2,
                        }
                    }}
                >
                    <DialogTitle>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <InventoryIcon color="primary" />
                            <Typography variant="h6">
                                {editMode ? 'Edit Contract' : isRenewal ? 'Renew Contract' : 'Create New Contract'}
                            </Typography>
                        </Box>
                    </DialogTitle>
                    <DialogContent sx={{ p: 3 }}>
                        <form onSubmit={handleSubmit}>
                            <Grid container spacing={2}>
                                {/* Customer Information */}
                                <Grid item xs={12} sm={6} style={{ width: "30%" }}>
                                    <Autocomplete
                                        fullWidth
                                        options={employers || []}
                                        value={employers.find(emp => emp.name === formData.customerName) || null}
                                        getOptionLabel={(option) => option.name || ''}
                                        isOptionEqualToValue={(option, value) => option.id === value.id}
                                        onChange={handleEmployerSelection}
                                        onInputChange={(event, newInputValue) => {
                                            if (newInputValue.length >= 3) {
                                                handleSearchEmployer(newInputValue);
                                            }
                                        }}
                                        loading={loadingEmployers}
                                        filterOptions={(x) => x}
                                        renderOption={(props, option) => (
                                            <Box component="li" {...props}>
                                                <Box>
                                                    <Typography variant="body1">{option.name}</Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {option.company_name || 'No company'}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        )}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Customer Name"
                                                required
                                                fullWidth
                                                error={touched.customerName && !formData.customerName}
                                                helperText={touched.customerName && !formData.customerName ? "Customer Name is required" : "Type at least 3 characters to search"}
                                                onBlur={() => handleBlur('customerName')}
                                                InputProps={{
                                                    ...params.InputProps,
                                                    endAdornment: (
                                                        <>
                                                            {loadingEmployers ? <CircularProgress color="inherit" size={20} /> : null}
                                                            {params.InputProps.endAdornment}
                                                        </>
                                                    ),
                                                }}
                                                sx={{
                                                    '& .MuiFormHelperText-root.Mui-error': {
                                                        color: '#f44336'
                                                    },
                                                    '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: '#f44336'
                                                    },
                                                    '& .MuiInputLabel-root.Mui-error': {
                                                        color: '#f44336'
                                                    }
                                                }}
                                            />
                                        )}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6} style={{ width: "30%" }}>
                                    <TextField
                                        label="Company Name"
                                        name="companyName"
                                        value={formData.companyName}
                                        onChange={handleChange}
                                        required
                                        fullWidth
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6} style={{ width: "30%" }}>
                                    <TextField
                                        label="Email"
                                        name="email"
                                        type="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        required
                                        fullWidth
                                    />
                                </Grid>

                                <Grid item sm={6} style={{ width: "30%" }}>
                                    <PhoneInput
                                        name="phone"
                                        value={formData.phone}
                                        onChange={handlePhoneChange}
                                        inputProps={{
                                            style: {
                                                width: '100%',
                                            }
                                        }}

                                        required

                                        defaultCountry="US"
                                        international
                                        countryCallingCodeEditable={false}
                                        placeholder="Enter phone number"

                                    />

                                </Grid>

                                {/* Contract Details */}
                                <Grid item xs={12} sm={6} sx={{ width: "30%" }}>
                                    <FormControl fullWidth>
                                        <InputLabel>Subscription Package</InputLabel>
                                        <Select
                                            name="subscriptionPackage"
                                            value={formData.subscriptionPackage}
                                            label="Subscription Package"
                                            onChange={handlePackageChange}
                                            required
                                        >
                                            {subscriptionPackages.map(pkg => (
                                                <MenuItem key={pkg.id} value={pkg.id}>
                                                    <span style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                                                        <span>{pkg.package_name}</span>
                                                        <span style={{ color: '#888', marginLeft: 16, minWidth: 80, textAlign: 'right', fontSize: '0.95em', fontWeight: 400 }}>
                                                            ({pkg.category})
                                                        </span>
                                                    </span>
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={6} style={{ width: "30%" }}>
                                    <FormControl fullWidth>
                                        <InputLabel>Status</InputLabel>
                                        <Select
                                            name="status"
                                            value={formData.status}
                                            label="Status"
                                            onChange={handleChange}
                                            required
                                        >
                                            {['Active', 'Pending', 'Expired', 'Cancelled', 'Suspended'].map(status => (
                                                <MenuItem key={status} value={status}>{status}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={6} style={{ width: "30%" }}>
                                    <TextField
                                        label="Created By"
                                        name="createdBy"
                                        value={formData.createdBy}
                                        disabled
                                        InputLabelProps={{ shrink: true }}
                                        required
                                        fullWidth
                                        
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6} style={{ width: "30%" }}>
                                    <TextField
                                        label="Created Date"
                                        name="createdDate"
                                        type="date"
                                        value={formData.createdDate}
                                        onChange={handleChange}
                                        required
                                        fullWidth
                                        InputLabelProps={{ shrink: true }}
                                        // inputProps={{
                                        //     min: dayjs().format('YYYY-MM-DD') // Prevent past dates
                                        // }}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6} style={{ width: "30%" }}>
                                    <TextField
                                        label="Expiration Date"
                                        name="expirationDate"
                                        type="date"
                                        value={formData.expirationDate}
                                        onChange={handleChange}
                                        required
                                        fullWidth
                                        InputLabelProps={{ shrink: true }}
                                        // inputProps={{
                                        //     min: dayjs().format('YYYY-MM-DD') // Prevent past dates
                                        // }}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6} style={{ width: "30%" }}>
                                    <TextField
                                        label="Discount (%)"
                                        name="discount"
                                        type="number"
                                        value={formData.discount}
                                        onChange={handleChange}
                                        fullWidth
                                        inputProps={{ min: 0, max: 100, step: 0.1 }}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6} sx={{ width: "30%" }}>
                                    <FormControl fullWidth>
                                        <Tooltip >
                                            <div> {/* Wrapper div needed for Tooltip to work with disabled elements */}
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                                                    {/* <FormControlLabel
                                                        control={
                                                            <Checkbox
                                                                checked={formData.isTrial}
                                                                onChange={(e) => {
                                                                    const isTrial = e.target.checked;
                                                                    setFormData(prev => ({
                                                                        ...prev,
                                                                        isTrial,
                                                                        expirationDate: isTrial
                                                                            ? dayjs().add(3, 'days').format('YYYY-MM-DD')
                                                                            : dayjs().add(1, 'month').format('YYYY-MM-DD')
                                                                    }));
                                                                    if (isTrial) {
                                                                        const trialPackage = subscriptionPackages.find(pkg => pkg.package_name.toLowerCase().includes('trial'));
                                                                        if (trialPackage) {
                                                                            handlePackageChange({ target: { value: trialPackage.id } });
                                                                        }
                                                                    }
                                                                }}
                                                                color="primary"
                                                                disabled={!formData.subscriptionPackage ||
                                                                    !subscriptionPackages.find(pkg =>
                                                                        pkg.id === formData.subscriptionPackage)?.package_name.toLowerCase().includes('trial')
                                                                }
                                                            />
                                                        }
                                                        label={
                                                            <Typography
                                                                color={
                                                                    !formData.subscriptionPackage ||
                                                                        !subscriptionPackages.find(pkg =>
                                                                            pkg.id === formData.subscriptionPackage)?.package_name.toLowerCase().includes('trial')
                                                                        ? 'text.disabled'
                                                                        : 'inherit'
                                                                }
                                                            >
                                                                Trial Period (3 Days)
                                                            </Typography>
                                                        }
                                                    /> */}
                                                    <FormControlLabel
                                                        control={
                                                            <Checkbox
                                                                checked={formData.autoRenewal || false}
                                                                onChange={e => setFormData(prev => ({
                                                                    ...prev,
                                                                    autoRenewal: e.target.checked
                                                                }))}
                                                                color="primary"
                                                            />
                                                        }
                                                        label={"Auto Renewal"}
                                                    />
                                                </Box>
                                            </div>
                                        </Tooltip>
                                    </FormControl>
                                </Grid>
                            </Grid>

                            {/* Contract Items Section */}
                            {contractItems.length > 0 && (
                                <Box sx={{ mt: 3 }}>
                                    <Typography variant="h6" gutterBottom>
                                        Contract Items: {getSelectedPackageName()}
                                    </Typography>
                                    <TableContainer>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow sx={{ backgroundColor: "#056bbd" }}>
                                                    <TableCell sx={{ color: 'white', }}>Item Name</TableCell>
                                                    {/* <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Unit</TableCell> */}
                                                    <TableCell sx={{ color: 'white', }}>Price</TableCell>
                                                    <TableCell sx={{ color: 'white', }}>Base Qty</TableCell>
                                                    <TableCell sx={{ color: 'white', }}>Free Qty</TableCell>
                                                    <TableCell sx={{ color: 'white', }}>Extra Qty</TableCell>
                                                    <TableCell sx={{ color: 'white', }}>Total Qty</TableCell>
                                                    <TableCell sx={{ color: 'white', }}>Total Value</TableCell>
                                                    <TableCell sx={{ color: 'white', }}>Free Value</TableCell>
                                                    <TableCell sx={{ color: 'white', }}>Discount Price</TableCell>
                                                    <TableCell sx={{ color: 'white', }}>Final Total</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {contractItems.map((item, index) => {
                                                    const totalValue = (item.quantity + item.free_quantity + item.extra_quantity) * item.price;
                                                    const freeValue = item.free_quantity * item.price;
                                                    const discountPrice = freeValue;
                                                    const finalTotal = totalValue - discountPrice;

                                                    return (
                                                        <TableRow key={`${item.id}-${index}`}>                                                            <TableCell>
                                                            <Typography fontWeight="bold">
                                                                {item.item_name}
                                                            </Typography>
                                                            {/* <Typography variant="body2" color="text.secondary">
                                                                    ({item.item_id})
                                                                </Typography> */}
                                                        </TableCell>
                                                            {/* <TableCell>{item.unit_of_measure}</TableCell> */}
                                                            <TableCell>${Number(item.price || 0).toFixed(2)}</TableCell>
                                                            <TableCell>
                                                                <TextField
                                                                    type="number"
                                                                    value={item.quantity}
                                                                    disabled
                                                                    size="small"
                                                                    sx={{ width: 100 }}
                                                                    inputProps={{ min: 0 }}
                                                                />
                                                            </TableCell>
                                                            <TableCell>
                                                                <TextField
                                                                    type="number"
                                                                    value={item.free_quantity}
                                                                    onChange={(e) => handleQuantityChange(index, 'free_quantity', e.target.value)}
                                                                    size="small"
                                                                    sx={{ width: 100 }}
                                                                    inputProps={{ min: 0 }}
                                                                />
                                                            </TableCell>
                                                            <TableCell>
                                                                <TextField
                                                                    type="number"
                                                                    value={item.extra_quantity}
                                                                    onChange={(e) => handleQuantityChange(index, 'extra_quantity', e.target.value)}
                                                                    size="small"
                                                                    sx={{ width: 70 }}
                                                                    inputProps={{ min: 0 }}
                                                                />
                                                            </TableCell>
                                                            <TableCell>
                                                                {item.quantity + item.free_quantity + item.extra_quantity}
                                                            </TableCell>
                                                            <TableCell>
                                                                ${totalValue.toFixed(2)}
                                                            </TableCell>
                                                            <TableCell>
                                                                ${freeValue.toFixed(2)}
                                                            </TableCell>
                                                            <TableCell style={{ color: "red" }}>
                                                                ${discountPrice.toFixed(2)}
                                                            </TableCell>
                                                            <TableCell>
                                                                <Typography fontWeight="bold" color="success.main">
                                                                    ${finalTotal.toFixed(2)}
                                                                </Typography>
                                                            </TableCell>
                                                        </TableRow>
                                                    );
                                                })}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>                                    {/* Billing Summary */}
                                    <Box sx={{ mt: 3, p: 3, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                                        <Paper elevation={3} sx={{
                                            mt: 3,
                                            p: 3,
                                            bgcolor: 'background.paper',
                                            border: `1px solid ${theme.palette.divider}`
                                        }}>
                                            <Typography variant="h6" gutterBottom sx={{
                                                fontWeight: 'bold',
                                                color: theme.palette.primary.main,
                                                pb: 1,
                                                borderBottom: `1px solid ${theme.palette.divider}`
                                            }}>
                                                Billing Summary
                                            </Typography>

                                            {/* Original Total (Sum of all items at full price) */}
                                            <Box sx={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                mb: 1
                                            }}>
                                                <Typography variant="body2">Total Original Amount:</Typography>
                                                <Typography variant="body2" fontWeight="medium">
                                                    ${contractItems.reduce((sum, item) =>
                                                        sum + ((item.quantity + item.free_quantity + item.extra_quantity) * item.price), 0
                                                    ).toFixed(2)}
                                                </Typography>
                                            </Box>

                                            {/* Free Items Section */}
                                            {contractItems.filter(item => item.free_quantity > 0).length > 0 && (
                                                <>
                                                    {contractItems.filter(item => item.free_quantity > 0).map((item, index) => (
                                                        <Box key={`free-item-${item.id || index}`} sx={{
                                                            display: 'flex',
                                                            justifyContent: 'space-between',
                                                            ml: 2
                                                        }}>
                                                            <Typography variant="body2">
                                                                Free {item.item_name} (Qty: {item.free_quantity})
                                                            </Typography>
                                                            <Typography variant="body2" color="success.main">
                                                                -${(item.free_quantity * item.price).toFixed(2)}
                                                            </Typography>
                                                        </Box>
                                                    ))}
                                                </>
                                            )}

                                            {/* Subtotal After Free Items */}
                                            <Box sx={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                mt: 1,
                                                mb: 1,
                                                pt: 1,
                                                borderTop: `1px dashed ${theme.palette.divider}`
                                            }}>
                                                <Typography variant="body2">Subtotal After Free Items:</Typography>
                                                <Typography variant="body2" fontWeight="medium">
                                                    ${contractItems.reduce((sum, item) =>
                                                        sum + ((item.quantity + item.extra_quantity) * item.price), 0
                                                    ).toFixed(2)}
                                                </Typography>
                                            </Box>

                                            {/* Contract Discount */}
                                            <Box sx={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                mb: 1
                                            }}>
                                                <Typography variant="body2">Contract Discount ({formData.discount}%):</Typography>
                                                <Typography variant="body2" color="error.main" fontWeight="medium">
                                                    -${(contractItems.reduce((sum, item) =>
                                                        sum + ((item.quantity + item.extra_quantity) * item.price), 0
                                                    ) * (formData.discount / 100).toFixed(2))}
                                                </Typography>
                                            </Box>

                                            {/* Final Total */}
                                            <Box sx={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                mt: 2,
                                                pt: 2,
                                                borderTop: `1px solid ${theme.palette.divider}`
                                            }}>
                                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                                    1 AMOUNT PAYABLE:
                                                </Typography>
                                                <Typography variant="subtitle1" sx={{
                                                    fontWeight: 'bold',
                                                    color: theme.palette.success.dark
                                                }}>
                                                    ${(contractItems.reduce((sum, item) =>
                                                        sum + ((item.quantity + item.extra_quantity) * item.price), 0
                                                    ) * (1 - (formData.discount / 100)).toFixed(2))}
                                                </Typography>
                                            </Box>

                                            {/* Summary Note */}
                                            <Typography variant="caption" color="text.secondary" sx={{
                                                display: 'block',
                                                mt: -1,
                                                fontStyle: 'italic'
                                            }}>
                                                Includes {contractItems.reduce((sum, item) => sum + item.free_quantity, 0)} free items
                                                {contractItems.filter(item => item.free_quantity > 0).length > 0 && (
                                                    <>
                                                        {' ('}                                                {contractItems.filter(item => item.free_quantity > 0)
                                                            .map(item => `${item.item_name}`)
                                                            .join(', ')}
                                                        {')'}
                                                    </>
                                                )}
                                            </Typography>
                                        </Paper>
                                    </Box>
                                </Box>
                            )}
                        </form>
                    </DialogContent>
                    <DialogActions sx={{ p: 3, pt: 0 }}>
                        <Button
                            onClick={() => {
                                resetForm();
                                setOpenFormDialog(false);
                            }}
                            color="inherit"
                            variant='outlined'
                            sx={{ textTransform: "none" }}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSubmit}
                            variant="contained"
                            color="primary"
                            disabled={loading.submit}
                            sx={{ px: 4, textTransform: "none" }}
                        >
                            {loading.submit ? (
                                <CircularProgress size={24} color="inherit" />
                            ) : (
                                editMode ? 'Update Contract' : isRenewal ? 'Renew Contract' : 'Create Contract'
                            )}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Delete Confirmation Dialog */}
                <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
                    <DialogTitle>Confirm Delete</DialogTitle>
                    <DialogContent>
                        <Typography>
                            Are you sure you want to delete the contract for {contractToDelete?.customerName}?
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
                        <Button
                            onClick={handleDelete}
                            color="error"
                            variant="contained"
                        >
                            Delete
                        </Button>
                    </DialogActions>
                </Dialog>
            </Container>
        </Box>
    );
};

export default CustomerContractForm;